import React from "react";
import { Flex, Stack, Text, Avatar } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";
import { getAvatarFallbackText } from "../../utils/publicAvatarUtils";
import colorsCustom from "../../designTokens/colorsCustom";

export const WriterInfo = ({
    profileImage,
    userID,
    username,
    name,
    sx,
    size = "big",
    showUserPageLink = false, // UserPage로 이동하는 링크 활성화
    showThreadsLink = false, // Threads.net으로 이동하는 링크 활성화
    useWhiteText = false, // 하얀색 텍스트 옵션 추가
}) => {
	const navigate = useNavigate();
	const { imageSrc, loading, error } = useCachedAvatar(userID, profileImage);

	// 사이즈 설정
	let avatarSize, textSize;
	switch (size) {
    case "huge":
      avatarSize = 64; // 아바타만 크게
      textSize = "lg"; // 텍스트는 big과 동일
      break;
		case "small":
			avatarSize = 24;
			textSize = "md";
			break;
		case "large":
			avatarSize = 40;
			textSize = "lg";
			break;
		case "big":
			avatarSize = 48;
			textSize = "lg";
			break;
		case "xl":
			avatarSize = 56;
			textSize = "xl";
			break;
		default:
			avatarSize = 32;
			textSize = "md";
			break;
	}

	const handleImageError = (event) => {
		console.warn(`⚠️ Avatar image error for user ${username}`);
		event.target.src = "";
		event.target.alt = getAvatarFallbackText(username);
	};

	const handleUserPageClick = () => {
		if (userID) {
			navigate(`/user/${userID}`);
		}
	};

	const handleThreadsLinkClick = () => {
		const threadsUrl = `https://www.threads.net/@${username}`;
		window.open(threadsUrl, "_blank", "noopener,noreferrer");
	};

	// 클릭 핸들러 결정 (showUserPageLink가 우선순위)
	const isClickable = showUserPageLink || showThreadsLink;
	const handleClickWithStop = (e) => {
		if (isClickable) {
			e.stopPropagation(); // 부모 요소(NavLink 등)의 클릭 이벤트 전파 막기
			if (showUserPageLink) {
				handleUserPageClick();
			} else if (showThreadsLink) {
				handleThreadsLinkClick();
			}
		}
	};

	return (
		<Flex
			direction={"row"}
			w={"100%"}
			align={"center"}
			gap={8}
			style={{
				borderRadius: 36,
				cursor: isClickable ? "pointer" : "default",
				transition: "all 0.2s ease",
				minWidth: 0,
				maxWidth: "100%",
				width: "100%",
				boxSizing: "border-box",
				overflow: "hidden",
				...sx,
			}}
			onClick={handleClickWithStop}
			onMouseEnter={(e) => {
				if (isClickable) {
					e.currentTarget.style.backgroundColor = colorsCustom.ui.dark.light;
					e.currentTarget.style.borderColor = colorsCustom.ui.dark.moderate;
				}
			}}
			onMouseLeave={(e) => {
				if (isClickable) {
					e.currentTarget.style.backgroundColor = "transparent";
					e.currentTarget.style.borderColor = colorsCustom.ui.dark.subtle;
				}
			}}
		>
			<Avatar
				src={imageSrc}
				size={avatarSize}
				onError={handleImageError}
				radius="xl"
				style={{
					flexShrink: 0,
				}}
			>
				{getAvatarFallbackText(username)}
			</Avatar>
			<Stack
				gap={8}
				style={{
					minWidth: 0,
					flex: 1,
					overflow: "hidden",
					width: 0,
				}}
			>
				<Text
					size={textSize}
					lh={1}
					fw={800}
					c={useWhiteText ? "#ffffff" : colorsCustom.ui.dark.bold}
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				>
					{username}
				</Text>
				<Text
					size={textSize}
					lh={1}
					c={useWhiteText ? "rgba(255, 255, 255, 0.7)" : colorsCustom.ui.dark.moderate}
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				>
					{name}
				</Text>
			</Stack>
		</Flex>
	);
};
